//
// # The migration
//
// The schema migration when one is in flight, and the one place that says
// whether there is one. Two states: `prev/` sits beside `current/` and this
// exports the edge between them, or there is no `prev/` and this exports null.
//
// It is always present so `depot_migrate.ts` can import it either way. The
// folder names are fixed, so its shape does not change between versions.
//
// See `.claude/skills/bump-schema-version/SKILL.md`.
//

import { Result } from "CoreUtils";
import { Content } from "./current";

export interface MigrationError {
  why: "invalid-at-source-version";
  from: string;
  detail: unknown;
}

/** What a migration may read besides the object itself: the source depot's PDK. */
export interface MigrationContext {
  /** The source depot's metal stacks, by stack id. */
  readonly stacks: ReadonlyMap<string, MigrationStack>;
}

export interface MigrationStack {
  readonly metalLayers: ReadonlyArray<{ layer: number; width: number }>;
}

/** Something the migration could not carry across on its own: the repair list. */
export interface MigrationNote {
  kind: "layout-pin";
  pin: string;
  reason:
    | "duplicate-pin-name"
    | "no-stack-width"
    | "no-metal-in-cell"
    | "no-metal-in-cell-has-children";
  detail?: string;
}

export interface Migrated {
  content: Content;
  notes: MigrationNote[];
}

export interface PendingMigration {
  readonly from: string;
  readonly to: string;
  /**
   * Validate a raw object at the source version, then migrate it.
   *
   * One call rather than two: the source version's concrete type only exists
   * while its folder does, so a two-step interface would have to hand
   * `unknown` between them and assert on the way.
   */
  migrate(
    raw: unknown,
    ctx: MigrationContext
  ): Result<Migrated, MigrationError>;
}

//
// # Steady state
//
// No `prev/` in the tree, nothing in flight.
//
// A bump replaces this with the edge it is introducing — see the
// `bump-schema-version` skill, which writes it. `finalize-schema-version.py`
// puts it back.
//
export const PENDING_MIGRATION: PendingMigration | null = null;
